import React, { useState } from "react";

import { useSelector } from "react-redux";

import {
  faChevronDown,
  faChevronUp,
  faPaperPlane,
  faPencil,
  faCheckCircle,
  faFilePen,
} from "@fortawesome/pro-solid-svg-icons";
import { faFilePdf } from "@fortawesome/pro-light-svg-icons";
import { Collapse, Divider, Grid } from "@material-ui/core";
import { MultiButton } from "components/Button";
import { MultiButtonOption } from "components/Button/MultiButton/types";
import IconTextButton from "components/Button/IconTextButton";
import Badge from "components/Badge";
import { PaddedPaper } from "components/Paper";
import styled from "styled-components/macro";
import { BoldText, Text } from "components/Text";
import StatusText from "components/Text/StatusText";

import { EContractParty, EContractTypeLabel } from "constants/eContract";
import { ModalTypes } from "constants/navigation";
import {
  DeploymentPermissions,
  EContractPermissions,
} from "constants/permissions";

import {
  getEContractStatus,
  getEContractStatusColor,
  getEContractCodeVersionAndStatusText,
} from "lib/econtracts";
import { getReportJobUrl, openModalLink } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import { getBuyerByEContractId, getVendorByEContractId } from "selectors";

import { useHasDeploymentPermission, useIsMobile } from "hooks";

import { EContractSendEmailModal } from "./sendEmailModal";
import { Row } from "components/Layout";
import { SmallFaIcon } from "components/FaIcon";
import { formatUTCToLocalDateString } from "lib/timeFormats";
import { colors } from "constants/theme";

interface EContractActionsProps {
  eContract: EContract;
  createHandler: (payload: {
    vendorBusinessId: string;
    buyerBusinessId: string;
  }) => void;
}

const EContractActions = ({
  eContract,
  createHandler,
}: EContractActionsProps): React.JSX.Element => {
  const [emailDialogParty, setEmailDialogParty] = useState(null);

  const hasCreatePermission = useHasDeploymentPermission(
    DeploymentPermissions.canCreateEContract,
  );
  const canRead: boolean = hasPermission(eContract, EContractPermissions.read);
  const canAccept: boolean = hasPermission(
    eContract,
    EContractPermissions.accept,
  );

  const isVoided = !!eContract.voidedOn;

  const buttons: MultiButtonOption[] = [
    isVoided && {
      title: "Draft New",
      default: isVoided,
      icon: faPencil,
      isDisabled: !hasCreatePermission,
      onClick: () =>
        createHandler({
          vendorBusinessId: eContract.vendorBusinessId,
          buyerBusinessId: eContract.buyerBusinessId,
        }),
    },
    {
      title: "View",
      icon: faFilePdf,
      isDisabled: !canRead,
      onClick: () => openModalLink(ModalTypes.EContract, { id: eContract.id }),
      default: !isVoided,
    },
    !isVoided && {
      title: "Send To Vendor",
      icon: faPaperPlane,
      isDisabled: !canAccept,
      onClick: () => setEmailDialogParty(EContractParty.VENDOR),
    },
    !isVoided && {
      title: "Send To Buyer",
      icon: faPaperPlane,
      isDisabled: !canAccept,
      onClick: () => setEmailDialogParty(EContractParty.BUYER),
    },
  ].filter(Boolean);

  const eContractStatus = getEContractStatus(eContract);

  return (
    <>
      <Grid container xs={12}>
        <Grid xs={6} container>
          <Badge
            color={getEContractStatusColor(eContractStatus)}
            bold
            uppercase
          >
            {eContractStatus}
          </Badge>
        </Grid>
        <Grid xs={6} container justifyContent="flex-end">
          <MultiButton buttons={buttons} />
        </Grid>
      </Grid>

      {emailDialogParty ? (
        <EContractSendEmailModal
          eContractId={eContract.id}
          onClose={() => setEmailDialogParty(null)}
          party={emailDialogParty}
        />
      ) : null}
    </>
  );
};

interface EContractCardProps {
  createHandler: (payload: {
    vendorBusinessId: string;
    buyerBusinessId: string;
  }) => void;
  eContracts: EContract[];
  userRoleSlug: string;
}

const PreviousVersion = ({
  eContract,
  userRoleSlug,
}: {
  eContract: EContract;
  userRoleSlug: string;
}): React.JSX.Element => {
  const documentPreviewUrl = getReportJobUrl(
    eContract.documentId,
    userRoleSlug,
  );
  return (
    <Row justifyBetween alignCenter padding={1} gridGap={2}>
      <StatusText fontSize="beta">
        {getEContractCodeVersionAndStatusText(eContract)}
      </StatusText>
      <IconTextButton
        disabled={
          !documentPreviewUrl ||
          !hasPermission(eContract, EContractPermissions.read)
        }
        icon={faFilePdf}
        inline
        onClick={() => window.open(documentPreviewUrl)}
      >
        <BoldText>Open PDF</BoldText>
      </IconTextButton>
    </Row>
  );
};

const EContractPaper = styled(PaddedPaper)<{ borderColor: typeof colors }>`
  border-left: ${({ theme }) => theme.space[1]}px solid
    ${({ theme, borderColor }) => theme.colors[borderColor]};
`;

const getDocumentStatusIcon = (acceptedOn, emailsCount) => {
  if (acceptedOn) {
    return <SmallFaIcon color="success" icon={faCheckCircle} />;
  }
  if (emailsCount) {
    return <SmallFaIcon color="inProgress" icon={faPaperPlane} />;
  }
  return <SmallFaIcon color="gray78" icon={faFilePen} />;
};

export const EContractSummaryCard = ({
  createHandler,
  eContracts,
  userRoleSlug,
}: EContractCardProps): React.JSX.Element | null => {
  const [showPreviousVersions, setShowPreviousVersions] = useState(false);
  const [latestEContract, ...previousEContracts] = eContracts;

  const buyer = useSelector(getBuyerByEContractId(latestEContract.id));
  const vendor = useSelector(getVendorByEContractId(latestEContract.id));

  const typeLabel =
    EContractTypeLabel[latestEContract.type] || latestEContract.type;

  const sentBuyerEmailsCount = latestEContract.buyerAcceptanceEmailIds?.length;
  const sentVendorEmailsCount =
    latestEContract.vendorAcceptanceEmailIds?.length;

  const acceptedByBuyer = !!latestEContract.buyerAcceptedOn;
  const acceptedByVendor = !!latestEContract.vendorAcceptedOn;

  const isMobile = useIsMobile();

  const version = `v${latestEContract.version}`;

  const latestEcontractStatus = getEContractStatus(latestEContract);

  return (
    <EContractPaper
      elevation={2}
      gap={2}
      fullHeight
      borderColor={getEContractStatusColor(latestEcontractStatus)}
    >
      <Grid
        container
        xs={12}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item xs={4} sm={8}>
          <Text fontSize="Charlie">
            {latestEContract.code} {version}
          </Text>
        </Grid>
        <Grid container xs={8} sm={4} justifyContent="flex-end">
          <Text italic>{typeLabel}</Text>
        </Grid>
        <Grid xs={12}>
          {latestEContract.voidedOn && (
            <BoldText color="errorRed">
              Voided On: {formatUTCToLocalDateString(latestEContract.voidedOn)}
            </BoldText>
          )}
          <Grid />
        </Grid>
      </Grid>

      <EContractActions
        eContract={latestEContract}
        createHandler={createHandler}
      />

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          md={6}
          container
          style={isMobile ? { marginBottom: "12px" } : {}}
        >
          <Grid item xs={3} sm={2} md={12}>
            <Text italic>
              {getDocumentStatusIcon(
                latestEContract.vendorAcceptedOn,
                sentVendorEmailsCount,
              )}{" "}
              Vendor
            </Text>
          </Grid>
          <Grid item xs={9} sm={10} md={12}>
            <Grid item xs={12}>
              <BoldText fontSize="beta">{vendor?.name ?? "-"}</BoldText>
            </Grid>
            <Grid item xs={12}>
              {acceptedByVendor && (
                <Row alignCenter gridGap={1}>
                  Accepted on:{" "}
                  {formatUTCToLocalDateString(latestEContract.vendorAcceptedOn)}
                </Row>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6} container>
          <Grid item xs={3} sm={2} md={12}>
            <Text italic>
              {getDocumentStatusIcon(
                latestEContract.buyerAcceptedOn,
                sentBuyerEmailsCount,
              )}{" "}
              Buyer
            </Text>
          </Grid>
          <Grid item xs={9} sm={10} md={12}>
            <Grid item xs={12}>
              <BoldText fontSize="beta">{buyer?.name ?? "-"}</BoldText>
            </Grid>
            <Grid item xs={12}>
              {acceptedByBuyer && (
                <Row alignCenter gridGap={1}>
                  Accepted on:{" "}
                  {formatUTCToLocalDateString(latestEContract.buyerAcceptedOn)}
                </Row>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {previousEContracts.length > 0 ? (
        <>
          <Grid container justifyContent="flex-end">
            <IconTextButton
              icon={showPreviousVersions ? faChevronUp : faChevronDown}
              iconPosition="right"
              inline
              onClick={() => setShowPreviousVersions(!showPreviousVersions)}
            >
              <BoldText>
                {showPreviousVersions ? "Hide" : "Show"} Previous Versions
              </BoldText>
            </IconTextButton>
          </Grid>
          <Collapse in={showPreviousVersions}>
            {previousEContracts.map(eContract => (
              <PreviousVersion
                eContract={eContract}
                key={eContract.id}
                userRoleSlug={userRoleSlug}
              />
            ))}
          </Collapse>
        </>
      ) : null}
    </EContractPaper>
  );
};
